import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import { AMC } from "../types/amc";
import { Loader2, AlertCircle } from "lucide-react";

interface ReportData {
  locationCode: string;
  oldLocationCode: string;
  nextVisitDate: string;
  lastVisitDate: string;
  daysUntilNextVisit: number;
  isPastDue: boolean;
}

export const ReportsPage = () => {
  const authToken = useSelector((state: any) => state.loginData?.authToken);
  const id = useSelector((state: any) => state.loginData?.id);
  const userRole = useSelector((state: any) => state.loginData?.role);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get today's date
  const getTodayDate = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Parse date string (YYYY-MM-DD)
  const parseDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // Calculate days until next visit
  const calculateDaysUntil = (dateString: string): number => {
    const today = getTodayDate();
    const visitDate = parseDate(dateString);
    const diffTime = visitDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if date is in the past
  const isPastDate = (dateString: string): boolean => {
    const today = getTodayDate();
    const visitDate = parseDate(dateString);
    return visitDate < today;
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Fetch and process AMC data
  const fetchAndProcessAMC = async () => {
    if (!authToken || !id) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/amc?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const amcRecords: AMC[] = response.data.amcRecords || [];

      // Group by location code and get the one with the closest future next_visit_date
      const groupedByLocation = new Map<string, AMC>();

      for (const amc of amcRecords) {
        const locationCode = amc.locationCode;

        // Only include records with valid next_visit_date
        if (!amc.nextVisitDate) continue;

        const daysUntil = calculateDaysUntil(amc.nextVisitDate);

        if (!groupedByLocation.has(locationCode)) {
          groupedByLocation.set(locationCode, amc);
        } else {
          const existing = groupedByLocation.get(locationCode)!;
          const existingDaysUntil = calculateDaysUntil(existing.nextVisitDate);

          // Keep the one with the closest future date (or closest past date if all are past)
          if (isPastDate(amc.nextVisitDate) && isPastDate(existing.nextVisitDate)) {
            // Both are in the past - keep the one closer to today
            if (daysUntil > existingDaysUntil) {
              groupedByLocation.set(locationCode, amc);
            }
          } else if (!isPastDate(amc.nextVisitDate) && isPastDate(existing.nextVisitDate)) {
            // Current is in future, existing is in past - keep current
            groupedByLocation.set(locationCode, amc);
          } else if (!isPastDate(amc.nextVisitDate) && !isPastDate(existing.nextVisitDate)) {
            // Both are in the future - keep the closer one
            if (daysUntil < existingDaysUntil) {
              groupedByLocation.set(locationCode, amc);
            }
          }
          // If current is in past and existing is in future - keep existing
        }
      }

      // Convert to report format and sort by next visit date (closest first)
      const report: ReportData[] = Array.from(groupedByLocation.values())
        .map((amc) => ({
          locationCode: amc.locationCode,
          oldLocationCode: amc.oldLocationCode,
          nextVisitDate: amc.nextVisitDate,
          lastVisitDate: amc.visitDate,
          daysUntilNextVisit: calculateDaysUntil(amc.nextVisitDate),
          isPastDue: isPastDate(amc.nextVisitDate),
        }))
        .sort((a, b) => {
          // Sort by days until next visit (closest first, including past dates)
          // Past dates should come first (negative numbers), then future dates
          if (a.isPastDue && !b.isPastDue) return -1; // Past dates first
          if (!a.isPastDue && b.isPastDue) return 1;
          return a.daysUntilNextVisit - b.daysUntilNextVisit;
        });

      setReportData(report);
    } catch (err: any) {
      console.error("Error fetching AMC data:", err);
      setError(
        err.response?.data?.message ||
          "Failed to fetch AMC data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    if (userRole !== "admin") {
      setError("You do not have permission to access this page.");
      return;
    }
    fetchAndProcessAMC();
  }, [authToken, id, userRole]);

  if (userRole !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          AMC Visit Schedule Report
        </h1>
        <p className="text-gray-600">
          Upcoming and overdue AMC visits by location, sorted by urgency
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-red-800 font-semibold">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="animate-spin text-blue-500 mx-auto mb-3" size={32} />
            <p className="text-gray-600">Loading report data...</p>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          {reportData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 text-lg">
                No AMC records found. Please add AMC visits first.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Table Header */}
                <thead>
                  <tr className="bg-gradient-to-r from-[#02474D] to-[#034E5A] text-white">
                    <th className="px-6 py-4 text-left font-semibold text-sm">
                      Location Code
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm">
                      Old Location Code
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm">
                      Last Visit Date
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm">
                      Next Visit Date
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm">
                      Status
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200">
                  {reportData.map((row, index) => {
                    const isPastDue = row.isPastDue;
                    const daysAway = Math.abs(row.daysUntilNextVisit);

                    return (
                      <tr
                        key={`${row.locationCode}-${index}`}
                        className={`hover:bg-gray-50 transition-colors ${
                          isPastDue ? "bg-red-50" : ""
                        }`}
                      >
                        {/* Location Code */}
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {row.locationCode}
                        </td>

                        {/* Old Location Code */}
                        <td className="px-6 py-4 text-gray-700">
                          {row.oldLocationCode || "-"}
                        </td>

                        {/* Last Visit Date */}
                        <td className="px-6 py-4 text-gray-700">
                          {formatDate(row.lastVisitDate)}
                        </td>

                        {/* Next Visit Date */}
                        <td
                          className={`px-6 py-4 font-semibold ${
                            isPastDue
                              ? "text-red-600 bg-red-100 rounded"
                              : "text-gray-900"
                          }`}
                        >
                          {formatDate(row.nextVisitDate)}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          {isPastDue ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                              ⚠️ Overdue ({daysAway} days)
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              ✓ In {daysAway} days
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary Footer */}
          {!loading && reportData.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Locations</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {reportData.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Overdue Visits</p>
                  <p className="text-2xl font-bold text-red-600">
                    {reportData.filter((r) => r.isPastDue).length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Due Within 30 Days</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {
                      reportData.filter(
                        (r) =>
                          !r.isPastDue &&
                          r.daysUntilNextVisit > 0 &&
                          r.daysUntilNextVisit <= 30
                      ).length
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
